<template>
  <section class="tag-cloud-section">
    <div class="container">
      <h2 class="tag-cloud-section__title">
        {{ $t('sections.tagCloud.title') }}
      </h2>
      <p class="tag-cloud-section__description">
        {{ $t('sections.tagCloud.description') }}
      </p>
      <div ref="container" class="tag-cloud-container">
        <div
          v-for="(tag, index) in tags"
          :key="index"
          :ref="
            el => {
              if (el) tagElements[index] = el
            }
          "
          class="tag"
          :style="tag.style"
        >
          {{ tag.text }}
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
import { useRafFn } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const tags = ref([])
const container = ref(null)
const tagElements = ref([])

const tagTexts = computed(() => [
  t('sections.tagCloud.tags.expenseControl'),
  t('sections.tagCloud.tags.nfseIssuance'),
  t('sections.tagCloud.tags.contractManagement'),
  t('sections.tagCloud.tags.managementReports'),
  t('sections.tagCloud.tags.accountsPayable'),
  t('sections.tagCloud.tags.accountsReceivable'),
  t('sections.tagCloud.tags.bankReconciliation'),
  t('sections.tagCloud.tags.multiBusiness'),
  t('sections.tagCloud.tags.multiCurrency'),
  t('sections.tagCloud.tags.financialDashboard'),
  t('sections.tagCloud.tags.statementImport'),
  t('sections.tagCloud.tags.invoiceDispatch'),
  t('sections.tagCloud.tags.billingAutomation'),
  t('sections.tagCloud.tags.budgetPlanning'),
  t('sections.tagCloud.tags.costCenter'),
  t('sections.tagCloud.tags.customerManagement'),
  t('sections.tagCloud.tags.inventoryControl'),
  t('sections.tagCloud.tags.dataSecurity'),
  t('sections.tagCloud.tags.accessLogs'),
  t('sections.tagCloud.tags.onlineSupport'),
  t('sections.tagCloud.tags.customization'),
  t('sections.tagCloud.tags.dynamicCharts'),
])

const initializeTags = async () => {
  const containerWidth = container.value
    ? container.value.offsetWidth
    : window.innerWidth
  const containerHeight = 400

  tags.value = tagTexts.value.map(text => ({
    text,
    x: Math.random() * (containerWidth - 100),
    y: Math.random() * (containerHeight - 40),
    vx: (Math.random() - 0.5) * 2,
    vy: (Math.random() - 0.5) * 2,
    width: 0,
    height: 0,
    style: {
      position: 'absolute',
      left: `0px`,
      top: `0px`,
      transform: `translate(0, 0)`,
      transition: 'transform 0.1s ease-out',
    },
  }))

  await nextTick()
  tagElements.value.forEach((el, i) => {
    if (el) {
      tags.value[i].width = el.offsetWidth
      tags.value[i].height = el.offsetHeight
    }
  })
}

watch(locale, initializeTags)

onMounted(async () => {
  await initializeTags()
  window.addEventListener('resize', initializeTags)
})

onUnmounted(() => {
  window.removeEventListener('resize', initializeTags)
})

useRafFn(() => {
  if (!container.value) return

  tags.value.forEach(tag => {
    if (tag.x - tag.width / 2 < 0) {
      tag.x = tag.width / 2
      tag.vx *= -0.7
    }
    if (tag.y - tag.height / 2 < 0) {
      tag.y = tag.height / 2
      tag.vy *= -0.7
    }
    if (tag.x + tag.width / 2 > container.value.offsetWidth) {
      tag.x = container.value.offsetWidth - tag.width / 2
      tag.vx *= -0.7
    }
    if (tag.y + tag.height / 2 > container.value.offsetHeight) {
      tag.y = container.value.offsetHeight - tag.height / 2
      tag.vy *= -0.7
    }

    tag.vx *= 1
    tag.vy *= 1

    tag.x += tag.vx
    tag.y += tag.vy

    tag.style.transform = `translate(${tag.x - tag.width / 3}px, ${
      tag.y - tag.height / 2
    }px)`
  })
})
</script>

<style scoped>
.tag-cloud-section {
  padding: 4rem 0;
  background-color: var(--c4);
  text-align: center;
  overflow: hidden;
}

.tag-cloud-section__title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--c1);
  margin-bottom: 1rem;
}

.tag-cloud-section__description {
  font-size: 1.2rem;
  color: var(--c3);
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.tag-cloud-container {
  position: relative;
  width: 100%;
  height: 400px;
  margin: 0 auto;
}

.tag {
  display: inline-block;
  padding: 0.5rem 1rem; /* Reduced padding */
  font-size: 0.9rem; /* Reduced font size */
  font-weight: 400; /* Thinner font weight */
  border-radius: 20px; /* Smaller border radius */
  background: linear-gradient(145deg, #e6e6e6, #ffffff);
  box-shadow: var(--shadow-sm);
  color: var(--c1);
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  background-clip: padding-box;
  transition: all 0.2s ease;
}

.tag:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-md);
  background: var(--gradient-primary);
  color: white;
}
</style>
